import { useCreateDataflow, useProjects } from "@/stores/database";
import { useDataflowStore } from "@/stores/dataflow";
import { logger } from "@/utils/debug";
import { Button, Flex, Modal, Select, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useState } from "react";
import { useNavigate } from "react-router";

export function WorkspaceModalDataflow({
  onClose,
  defaultProjectId,
  title = "Create New Workspace",
}: {
  onClose: () => void;
  defaultProjectId?: string;
  title?: string;
}) {
  const projects = useProjects();
  const createDataflow = useCreateDataflow();
  const setActiveDataflow = useDataflowStore(
    (state) => state.setActiveDataflow
  );
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const form = useForm({
    initialValues: {
      title: "",
      project_id: defaultProjectId || "",
    },
    validate: {
      title: (value) =>
        value.trim().length === 0 ? "Workspace name is required" : null,
      project_id: (value) => (!value ? "Project is required" : null),
    },
  });

  const handleSubmit = async (values: {
    title: string;
    project_id: string;
  }) => {
    setLoading(true);
    try {
      const { error, data } = await createDataflow({
        dataflow_name: values.title.trim(),
        project_id: values.project_id,
        dataflow_description: "",
        status: "draft",
        flow_definition_json: [],
      });

      if (error) {
        logger.error("Failed to create workspace:", error);
        // You could add a notification here
      } else {
        form.reset();
        onClose();
        if (data?.dataflow_id) {
          setActiveDataflow(data.dataflow_id);
          navigate(`/build/${values.project_id}/${data.dataflow_id}`);
        }
      }
    } catch (err) {
      logger.error("Error creating dataflow:", err);
    } finally {
      setLoading(false);
    }
  };

  // Prepare project options for the Select component
  const projectOptions = projects.map((project) => ({
    value: project.id,
    label: project.title,
  }));

  return (
    <Modal opened={true} onClose={onClose} title={title} centered size="sm">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Flex direction="column" gap="md">
          <Select
            placeholder="Select a project"
            data={projectOptions}
            {...form.getInputProps("project_id")}
            searchable={false}
            clearable={false}
            allowDeselect={false}
            required
          />
          <TextInput
            placeholder="Enter workspace name"
            {...form.getInputProps("title")}
            required
            autoFocus
          />
          <Flex justify="flex-end" gap="sm">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" loading={loading}>
              Create Workspace
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
}

